import { decouperLiens, decrirePage, type PageAppli } from '@admin-pmvb/core'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter, useRouterState } from '@tanstack/react-router'
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { api, lire } from '../api'
import { marquerOccupee } from '../pwa/occupation'
import { detecterClaudeInChrome, raccourciClaude } from './claude-in-chrome'
import { Icone } from './Icone'

// Assistant : panneau à droite, ouvert sur toutes les pages de l'appli (monté dans la
// coquille), qui ouvre un chat avec Claude. Il voit la page ouverte : son adresse part avec
// chaque message, et l'API y joint la fiche d'une tâche ou d'une pièce quand elle a changé.
// Il lit l'aide et agit par les outils du MCP avec les droits de la personne ; il agit sans
// attendre, sauf une suppression ou l'affirmation d'un geste humain (payé, fait), qui attend
// ici son accord. Ouvert ou fermé, agrandi ou non, et sa largeur (poignée de son bord gauche)
// sont retenus pour l'appareil ; la conversation (messages de l'API Claude, renvoyés tels quels
// à chaque tour) l'est pour l'onglet, d'une page à l'autre. Sur téléphone, il prend l'écran.
// Là où Claude in Chrome est installé, l'appli passe par lui (abonnement, pas l'API) : fermé,
// le bouton rappelle son raccourci, et l'assistant de l'appli reste à un clic de plus.

type Bloc = { type: string; [cle: string]: unknown }
type Message = { role: 'user' | 'assistant'; content: string | Bloc[] }
type Action = { id: string; nom: string; titre: string; entree: unknown }
type Conversation = { historique: Message[]; enAttente: Action[] }
type EtatOutil = 'fait' | 'erreur' | 'refuse'
type Evenement =
  | { type: 'texte'; texte: string }
  | { type: 'outil'; id: string; nom: string; titre: string; etat: EtatOutil }
  | { type: 'fin'; historique: Message[]; enAttente: Action[]; message?: string }
  | { type: 'erreur'; message: string }

type EtapeEnCours = { titre: string; etat: EtatOutil }

const requeteAssistant = queryOptions({
  queryKey: ['assistant'],
  queryFn: () => lire(api.assistant.$get()),
  staleTime: Infinity,
})

const VIDE: Conversation = { historique: [], enAttente: [] }
const CLE_CONVERSATION = 'assistant:conversation'
const CLE_REGLAGE = 'assistant:panneau'

function charger(): Conversation {
  try {
    const brut = sessionStorage.getItem(CLE_CONVERSATION)
    return brut ? (JSON.parse(brut) as Conversation) : VIDE
  } catch {
    return VIDE
  }
}

function garder(c: Conversation) {
  try {
    if (c.historique.length) sessionStorage.setItem(CLE_CONVERSATION, JSON.stringify(c))
    else sessionStorage.removeItem(CLE_CONVERSATION)
  } catch {
    /* stockage indisponible ou plein : la conversation vit le temps de la page */
  }
}

/** Le panneau tel que la personne l'a laissé sur cet appareil. */
type Reglage = { ouvert: boolean; agrandi: boolean; largeur: number }

const LARGEUR_DEFAUT = 420
const LARGEUR_MIN = 320
/** Ce qui reste au moins à la page à côté du panneau (barre latérale comprise). */
const RESTE_PAGE = 236 + 480

/** Téléphone et tablette : le panneau passe par-dessus la page au lieu de s'y ranger. */
const parDessus = () => window.matchMedia('(max-width: 900px)').matches

const bornee = (largeur: number) =>
  Math.round(Math.max(LARGEUR_MIN, Math.min(largeur, window.innerWidth - RESTE_PAGE, 1400)))

function chargerReglage(): Reglage {
  const defaut = { ouvert: false, agrandi: false, largeur: LARGEUR_DEFAUT }
  try {
    const r = JSON.parse(localStorage.getItem(CLE_REGLAGE) ?? 'null') as Partial<Reglage> | null
    return {
      // Rouvert au chargement à côté de la page, jamais par-dessus (il cacherait l'écran).
      ouvert: r?.ouvert === true && !parDessus(),
      agrandi: r?.agrandi === true,
      largeur: typeof r?.largeur === 'number' ? r.largeur : LARGEUR_DEFAUT,
    }
  } catch {
    return defaut
  }
}

function garderReglage(r: Reglage) {
  try {
    localStorage.setItem(CLE_REGLAGE, JSON.stringify(r))
  } catch {
    /* stockage indisponible : le panneau revient fermé, à sa largeur par défaut */
  }
}

/** Largeur agrandie : la moitié de l'écran au moins, sans écraser la page. */
const largeurAgrandie = (largeur: number) =>
  bornee(Math.max(largeur, Math.round(window.innerWidth * 0.55)))

/** Envoie un tour et lit la réponse en flux (SSE) ; un échec avant le flux lève une erreur. */
async function tour(corps: unknown, signal: AbortSignal, surEvenement: (e: Evenement) => void) {
  const r = await fetch('/api/assistant', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(corps),
    signal,
  })
  if (!r.ok || !r.body) {
    const e = (await r.json().catch(() => ({}))) as { erreur?: string }
    throw new Error(e.erreur ?? `Erreur ${r.status}`)
  }
  const lecteur = r.body.pipeThrough(new TextDecoderStream()).getReader()
  let tampon = ''
  for (;;) {
    const { value, done } = await lecteur.read()
    if (done) break
    tampon += value
    let fin = tampon.indexOf('\n\n')
    while (fin >= 0) {
      const donnee = tampon
        .slice(0, fin)
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n')
      tampon = tampon.slice(fin + 2)
      if (donnee) surEvenement(JSON.parse(donnee) as Evenement)
      fin = tampon.indexOf('\n\n')
    }
  }
}

/**
 * Ce que la personne a écrit : le contexte de la page, la marque du document d'une pièce et
 * les résultats d'outils sont cachés.
 */
function texteHumain(m: Message): string | null {
  if (typeof m.content === 'string') return m.content
  const textes = m.content
    .filter((b) => b.type === 'text' && !/^<(contexte>|fichier )/.test(String(b.text)))
    .map((b) => String(b.text))
  return textes.length ? textes.join('\n') : null
}

/** Issue de chaque appel d'outil, lue dans les résultats renvoyés à Claude. */
function issues(historique: Message[]): Map<string, EtatOutil> {
  const r = new Map<string, EtatOutil>()
  for (const m of historique) {
    if (m.role !== 'user' || typeof m.content === 'string') continue
    for (const b of m.content) {
      if (b.type !== 'tool_result') continue
      const texte = typeof b.content === 'string' ? b.content : ''
      r.set(
        String(b.tool_use_id),
        b.is_error ? (texte.startsWith('Refusé') ? 'refuse' : 'erreur') : 'fait',
      )
    }
  }
  return r
}

const LIBELLE_ETAT: Record<EtatOutil | 'attente', string> = {
  fait: 'fait',
  erreur: 'en erreur',
  refuse: 'refusé',
  attente: 'en attente de votre accord',
}

function Etape({ titre, etat }: { titre: string; etat: EtatOutil | 'attente' }) {
  const icone = etat === 'fait' ? 'check' : etat === 'attente' ? 'hourglass_top' : 'close'
  return (
    <li className={`assistant-etape assistant-etape-${etat}`}>
      <Icone nom={icone} taille={15} /> {titre}
      <span className="visuellement-cache"> : {LIBELLE_ETAT[etat]}</span>
    </li>
  )
}

/** Arguments d'une action proposée, lisibles : une ligne par champ. */
function Details({ entree }: { entree: unknown }) {
  if (!entree || typeof entree !== 'object') return null
  const lignes = Object.entries(entree as Record<string, unknown>)
  if (!lignes.length) return null
  return (
    <dl className="assistant-details">
      {lignes.map(([k, v]) => (
        <div key={k}>
          <dt>{k.replace(/_/g, ' ')}</dt>
          <dd>{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Un message de Claude : les adresses de pages qu'il écrit deviennent des liens. */
function TexteAvecLiens({ texte, suivi }: { texte: string; suivi: () => void }) {
  const router = useRouter()
  return decouperLiens(texte).map((m, i) =>
    'texte' in m ? (
      m.texte
    ) : (
      <a
        key={i}
        href={m.chemin}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
          e.preventDefault()
          void router.navigate({ href: m.chemin })
          suivi()
        }}
      >
        {decrirePage(m.chemin).libelle}
      </a>
    ),
  )
}

function suggestions(page: PageAppli): string[] {
  if (page.objet?.type === 'tache')
    return [
      'Que dois-je faire, concrètement ?',
      'Qu’est-ce qui manque pour la terminer ?',
      'Prépare un brouillon de réponse.',
    ]
  if (page.objet?.type === 'piece')
    return ['Résume cette pièce.', 'Que dois-je en faire ?', 'Est-elle bien rattachée et rangée ?']
  return [
    'Que puis-je faire sur cette page ?',
    'Qu’est-ce qui est urgent cette semaine ?',
    'Résume ce que montre cette page.',
  ]
}

/** Le panneau de l'assistant : une fois, dans la coquille des pages connectées. */
export function PanneauAssistant() {
  const config = useQuery(requeteAssistant)
  const client = useQueryClient()
  const chemin = useRouterState({
    select: (e) => `${e.location.pathname}${e.location.searchStr}`,
  })
  const page = decrirePage(chemin)
  const [reglage, setReglage] = useState<Reglage>(chargerReglage)
  // Seulement pour redessiner quand la fenêtre change (lue dans l'effet : rendu hors navigateur).
  const [, setLargeurEcran] = useState(0)
  const [redimensionne, setRedimensionne] = useState(false)
  const [conv, setConv] = useState<Conversation>(charger)
  const [saisie, setSaisie] = useState('')
  const [enCours, setEnCours] = useState<{
    texte: string
    etapes: Map<string, EtapeEnCours>
  } | null>(null)
  const [refus, setRefus] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState<string | null>(null)
  // Claude in Chrome : inconnu (rien d'affiché) jusqu'à la réponse, lue après le premier rendu.
  const [claudeChrome, setClaudeChrome] = useState<boolean | null>(null)
  const [aide, setAide] = useState(false)
  const arret = useRef<AbortController | null>(null)
  const fil = useRef<HTMLDivElement>(null)
  const zone = useRef<HTMLTextAreaElement>(null)
  // La saisie prend le curseur quand la personne ouvre le panneau, pas au chargement.
  const focaliser = useRef(false)
  const { ouvert, agrandi } = reglage

  // Les bornes de la largeur suivent la fenêtre.
  useEffect(() => {
    const suivre = () => setLargeurEcran(window.innerWidth)
    window.addEventListener('resize', suivre)
    return () => window.removeEventListener('resize', suivre)
  }, [])

  useEffect(() => {
    if (!ouvert || !focaliser.current) return
    focaliser.current = false
    zone.current?.focus()
  }, [ouvert])

  useEffect(() => {
    fil.current?.scrollTo({ top: fil.current.scrollHeight })
  }, [conv, enCours, ouvert])

  useEffect(() => {
    marquerOccupee('assistant', enCours !== null)
    return () => marquerOccupee('assistant', false)
  }, [enCours])

  useEffect(() => () => arret.current?.abort(), [])

  useEffect(() => {
    let actif = true
    void detecterClaudeInChrome(globalThis.fetch?.bind(globalThis)).then((oui) => {
      if (actif) setClaudeChrome(oui)
    })
    return () => {
      actif = false
    }
  }, [])

  if (!config.data?.disponible) return null
  const titres = config.data.outils as Record<string, string>

  function regler(r: Partial<Reglage>) {
    setReglage((avant) => {
      const apres = { ...avant, ...r }
      garderReglage(apres)
      return apres
    })
  }

  // Largeur réglée à la poignée du bord gauche (souris, doigt ou flèches du clavier) ; la
  // garder la retient et quitte l'agrandi.
  const largeur = agrandi ? largeurAgrandie(reglage.largeur) : bornee(reglage.largeur)
  function tirer(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return
    e.preventDefault()
    const poignee = e.currentTarget
    poignee.setPointerCapture(e.pointerId)
    setRedimensionne(true)
    let derniere = largeur
    const suivre = (m: globalThis.PointerEvent) => {
      derniere = bornee(window.innerWidth - m.clientX)
      setReglage((r) => ({ ...r, agrandi: false, largeur: derniere }))
    }
    const lacher = () => {
      poignee.removeEventListener('pointermove', suivre)
      poignee.removeEventListener('pointerup', lacher)
      poignee.removeEventListener('pointercancel', lacher)
      setRedimensionne(false)
      regler({ agrandi: false, largeur: derniere })
    }
    poignee.addEventListener('pointermove', suivre)
    poignee.addEventListener('pointerup', lacher)
    poignee.addEventListener('pointercancel', lacher)
  }
  function toucheLargeur(e: KeyboardEvent<HTMLDivElement>) {
    const pas = e.shiftKey ? 80 : 24
    const cible =
      e.key === 'ArrowLeft'
        ? largeur + pas
        : e.key === 'ArrowRight'
          ? largeur - pas
          : e.key === 'Home'
            ? LARGEUR_DEFAUT
            : null
    if (cible === null) return
    e.preventDefault()
    regler({ agrandi: false, largeur: bornee(cible) })
  }

  /** Un tour : `depart` est la conversation envoyée, rétablie si le tour n'aboutit pas. */
  async function avancer(
    depart: Conversation,
    corps: { message?: string; decisions?: Record<string, boolean> },
  ) {
    const controleur = new AbortController()
    arret.current = controleur
    setMessage(null)
    setEnCours({ texte: '', etapes: new Map() })
    let fin: Conversation | null = null
    try {
      await tour(
        { page: chemin, historique: depart.historique, ...corps },
        controleur.signal,
        (e) => {
          if (e.type === 'texte') setEnCours((c) => c && { ...c, texte: c.texte + e.texte })
          else if (e.type === 'outil')
            setEnCours((c) => {
              if (!c) return c
              const etapes = new Map(c.etapes)
              etapes.set(e.id, { titre: e.titre, etat: e.etat })
              return { texte: '', etapes }
            })
          else if (e.type === 'fin') {
            fin = { historique: e.historique, enAttente: e.enAttente }
            if (e.message) setMessage(e.message)
          } else setMessage(e.message)
        },
      )
    } catch (err) {
      if (!controleur.signal.aborted)
        setMessage(err instanceof Error ? err.message : 'L’assistant ne répond pas.')
    } finally {
      setEnCours(null)
      arret.current = null
    }
    if (fin) {
      setConv(fin)
      garder(fin)
      setRefus(new Set())
      // Les outils ont pu changer la page (statut, rattachement, commentaire).
      void client.invalidateQueries()
    } else {
      // Arrêté ou en échec : rien n'a été gardé côté serveur, on revient à l'état d'avant.
      setConv(depart)
      if (corps.message) setSaisie(corps.message)
    }
  }

  function envoyer(e?: FormEvent) {
    e?.preventDefault()
    const texte = saisie.trim()
    if (!texte || enCours || conv.enAttente.length) return
    setSaisie('')
    // Affiché tout de suite ; l'historique renvoyé par l'API (qui l'ajoute) le remplacera.
    setConv({ ...conv, historique: [...conv.historique, { role: 'user', content: texte }] })
    void avancer(conv, { message: texte })
  }

  function touche(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) envoyer()
    if (e.key === 'Enter' && !e.shiftKey) e.preventDefault()
  }

  function decider(toutRefuser: boolean) {
    const decisions = Object.fromEntries(
      conv.enAttente.map((a) => [a.id, !toutRefuser && !refus.has(a.id)]),
    )
    void avancer(conv, { decisions })
  }

  function recommencer() {
    arret.current?.abort()
    setConv(VIDE)
    garder(VIDE)
    setMessage(null)
    zone.current?.focus()
  }

  const etats = issues(conv.historique)
  const attente = new Set(conv.enAttente.map((a) => a.id))

  if (!ouvert && claudeChrome === null) return null
  if (!ouvert && claudeChrome) {
    const raccourci = raccourciClaude(navigator.platform)
    return (
      <>
        {aide && (
          <div className="assistant-claude-aide" id="assistant-claude-aide" role="note">
            <p>
              Claude in Chrome s’ouvre à droite avec <kbd>{raccourci}</kbd>, ou l’icône Claude de la
              barre de Chrome. Il voit cette page et passe par votre abonnement.
            </p>
            <button
              type="button"
              className="bouton petit"
              onClick={() => {
                setAide(false)
                focaliser.current = true
                regler({ ouvert: true })
              }}
            >
              Ouvrir plutôt l’assistant de l’appli (payé à l’API)
            </button>
          </div>
        )}
        <button
          type="button"
          className="assistant-bouton"
          onClick={() => setAide((a) => !a)}
          aria-expanded={aide}
          aria-controls="assistant-claude-aide"
          title={`Claude in Chrome : ${raccourci}`}
        >
          <Icone nom="smart_toy" taille={22} />
          <span>Claude {raccourci}</span>
        </button>
      </>
    )
  }

  if (!ouvert)
    return (
      <button
        type="button"
        className="assistant-bouton"
        onClick={() => {
          focaliser.current = true
          regler({ ouvert: true })
        }}
        aria-expanded={false}
        aria-controls="assistant-panneau"
      >
        <Icone nom="smart_toy" taille={22} />
        <span>Assistant</span>
      </button>
    )

  return (
    <aside
      id="assistant-panneau"
      className={`assistant-panneau${agrandi ? ' agrandi' : ''}${redimensionne ? ' redimensionne' : ''}`}
      style={{ '--largeur-assistant': `${largeur}px` } as CSSProperties}
      aria-label="Assistant"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !enCours) regler({ ouvert: false })
      }}
    >
      <div
        className="assistant-poignee"
        role="separator"
        aria-orientation="vertical"
        aria-label="Largeur de l’assistant"
        aria-valuenow={largeur}
        aria-valuemin={LARGEUR_MIN}
        aria-valuemax={bornee(Infinity)}
        tabIndex={0}
        title="Glisser pour élargir ou rétrécir (double-clic : largeur par défaut)"
        onPointerDown={tirer}
        onKeyDown={toucheLargeur}
        onDoubleClick={() => regler({ agrandi: false, largeur: LARGEUR_DEFAUT })}
      />
      <header className="assistant-entete">
        <span className="assistant-avatar">
          <Icone nom="smart_toy" taille={20} />
        </span>
        <div className="assistant-entete-texte">
          <strong>Assistant</strong>
          <span title={chemin}>Voit : {page.libelle}</span>
        </div>
        {conv.historique.length > 0 && (
          <button
            type="button"
            className="bouton-icone"
            onClick={recommencer}
            title="Nouvelle conversation"
            aria-label="Nouvelle conversation"
          >
            <Icone nom="restart_alt" taille={19} />
          </button>
        )}
        <button
          type="button"
          className="bouton-icone assistant-agrandir"
          onClick={() => regler({ agrandi: !agrandi })}
          title={agrandi ? 'Réduire' : 'Agrandir'}
          aria-label={agrandi ? 'Réduire l’assistant' : 'Agrandir l’assistant'}
          aria-pressed={agrandi}
        >
          <Icone nom={agrandi ? 'close_fullscreen' : 'open_in_full'} taille={18} />
        </button>
        <button
          type="button"
          className="bouton-icone"
          onClick={() => regler({ ouvert: false })}
          title="Fermer"
          aria-label="Fermer l’assistant"
        >
          <Icone nom="close" taille={19} />
        </button>
      </header>

      <div className="assistant-fil" ref={fil} aria-live="polite">
        {conv.historique.length === 0 && !enCours && (
          <div className="assistant-accueil">
            <p>
              Je vois la page que vous ouvrez et je connais l’aide de l’appli et ses outils. Je
              range et fais avancer moi-même, vous corrigez ensuite si besoin ; je ne demande votre
              accord que pour supprimer ou marquer payé ou fait. Payer, déclarer et signer restent à
              vous.
            </p>
            <div className="assistant-suggestions">
              {suggestions(page).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="bouton-discret"
                  onClick={() => {
                    setSaisie(s)
                    zone.current?.focus()
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {conv.historique.map((m, i) => {
          if (m.role === 'user') {
            const texte = texteHumain(m)
            return texte ? (
              <p key={i} className="assistant-bulle assistant-bulle-moi">
                {texte}
              </p>
            ) : null
          }
          const blocs =
            typeof m.content === 'string' ? [{ type: 'text', text: m.content }] : m.content
          const etapes = blocs.filter((b) => b.type === 'tool_use')
          return (
            <div key={i} className="assistant-tour">
              {blocs
                .filter((b) => b.type === 'text' && String(b.text).trim())
                .map((b, j) => (
                  <p key={j} className="assistant-bulle">
                    <TexteAvecLiens
                      texte={String(b.text)}
                      suivi={() => {
                        // Par-dessus la page, le panneau s'efface pour la montrer.
                        if (parDessus()) regler({ ouvert: false })
                      }}
                    />
                  </p>
                ))}
              {etapes.length > 0 && (
                <ul className="assistant-etapes">
                  {etapes.map((b) => {
                    const id = String(b.id)
                    return (
                      <Etape
                        key={id}
                        titre={titres[String(b.name)] ?? String(b.name)}
                        etat={etats.get(id) ?? (attente.has(id) ? 'attente' : 'erreur')}
                      />
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
        {enCours && (
          <div className="assistant-tour">
            {enCours.etapes.size > 0 && (
              <ul className="assistant-etapes">
                {[...enCours.etapes].map(([id, e]) => (
                  <Etape key={id} titre={e.titre} etat={e.etat} />
                ))}
              </ul>
            )}
            {enCours.texte ? (
              <p className="assistant-bulle">{enCours.texte}</p>
            ) : (
              <p className="assistant-reflexion discret">L’assistant réfléchit…</p>
            )}
          </div>
        )}
        {message && <p className="assistant-message alerte">{message}</p>}
      </div>

      {conv.enAttente.length > 0 && !enCours && (
        <div className="assistant-accord">
          <p>
            <strong>
              {conv.enAttente.length === 1
                ? 'Action proposée'
                : `${conv.enAttente.length} actions proposées`}
            </strong>{' '}
            : rien n’est fait sans votre accord.
          </p>
          <ul>
            {conv.enAttente.map((a) => (
              <li key={a.id}>
                <label className="assistant-action">
                  {conv.enAttente.length > 1 && (
                    <input
                      type="checkbox"
                      checked={!refus.has(a.id)}
                      onChange={(e) =>
                        setRefus((r) => {
                          const n = new Set(r)
                          if (e.target.checked) n.delete(a.id)
                          else n.add(a.id)
                          return n
                        })
                      }
                    />
                  )}
                  <span>{a.titre}</span>
                </label>
                <Details entree={a.entree} />
              </li>
            ))}
          </ul>
          <div className="ligne-actions">
            <button type="button" className="bouton" onClick={() => decider(false)}>
              <Icone nom="check" />
              {conv.enAttente.length === 1 ? 'Accepter' : 'Exécuter la sélection'}
            </button>
            <button type="button" className="bouton-discret" onClick={() => decider(true)}>
              {conv.enAttente.length === 1 ? 'Refuser' : 'Tout refuser'}
            </button>
          </div>
        </div>
      )}

      <form className="assistant-saisie" onSubmit={envoyer}>
        <textarea
          ref={zone}
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={touche}
          rows={2}
          maxLength={8000}
          placeholder={
            conv.enAttente.length
              ? 'Accepter ou refuser d’abord l’action proposée'
              : 'Votre question…'
          }
          aria-label="Message à l’assistant"
          disabled={conv.enAttente.length > 0 && !enCours}
        />
        {enCours ? (
          <button
            type="button"
            className="bouton-icone"
            onClick={() => arret.current?.abort()}
            title="Arrêter"
            aria-label="Arrêter"
          >
            <Icone nom="stop_circle" taille={22} />
          </button>
        ) : (
          <button
            type="submit"
            className="bouton-icone assistant-envoyer"
            disabled={!saisie.trim() || conv.enAttente.length > 0}
            title="Envoyer"
            aria-label="Envoyer"
          >
            <Icone nom="send" taille={20} />
          </button>
        )}
      </form>
    </aside>
  )
}
